/**
 * What of the heat pump info (port 0x23) is shown to the user, and how.
 *
 * Fields that did not work on the first real unit (Midea all-in-one on
 * BB15F92E, doc/heatpump_midea_test_2026-09.md in the firmware repo) are not
 * shown, even when the unit reports a value:
 *  - buffer tank temperature: 0xFF / not available without a buffer sensor,
 *  - room temperature and zone 2 flow: the unit reports a fake 25 degC when absent,
 *  - DHW tank heater (TBH) flag: the all-in-one has no tank heater,
 *  - external heat source flag: none,
 *  - compressor target byte: reads 0/1 on the Hygge, not a frequency,
 *  - machine heat energy counter: not a lifetime counter,
 *  - SG-ready bits: SG is disabled on the unit.
 * On top of that, any temperature the unit reports as not available is hidden.
 */
import { HeatPumpFlag, HeatPumpMode, HeatPumpStatus, HeatPumpTemperature, PortValuesHeatPump } from '../../types/ports'

/** Temperatures shown, in display order, with their translation keys. */
export const HEAT_PUMP_SHOWN_TEMPERATURES: [HeatPumpTemperature, string][] = [
  ['t_flow', 'HeatPump-Flow'],
  ['t_flow_setpoint', 'HeatPump-Flow setpoint'],
  ['t_return', 'HeatPump-Return'],
  ['t_outdoor', 'HeatPump-Outdoor'],
  ['t_dhw_tank', 'HeatPump-Hot water tank'],
  ['t_dhw_setpoint', 'HeatPump-Hot water setpoint'],
]

/** Flags shown (when set), in display order, with their translation keys. */
export const HEAT_PUMP_SHOWN_FLAGS: [HeatPumpFlag, string][] = [
  ['compressor_on', 'HeatPump-Compressor on'],
  ['defrost', 'HeatPump-Defrost'],
  ['dhw_valve', 'HeatPump-Valve to hot water'],
  ['pump_on', 'HeatPump-Circulation pump on'],
  ['aux_space_on', 'HeatPump-Electric backup heater on'],
  ['under_control', 'HeatPump-Under Enion control'],
]

export const HEAT_PUMP_MODE_KEYS: Record<HeatPumpMode, string> = {
  HEATPUMP_MODE_OFF: 'HeatPumpMode-OFF',
  HEATPUMP_MODE_HEAT: 'HeatPumpMode-HEAT',
  HEATPUMP_MODE_COOL: 'HeatPumpMode-COOL',
  HEATPUMP_MODE_DHW: 'HeatPumpMode-DHW',
  HEATPUMP_MODE_UNKNOWN: 'HeatPumpMode-UNKNOWN',
}

export const HEAT_PUMP_STATUS_KEYS: Record<HeatPumpStatus, string> = {
  SPP_HEATPUMP_INFO_STATUS_OK: 'HeatPumpStatus-OK',
  SPP_HEATPUMP_INFO_STATUS_COMM_FAILURE: 'HeatPumpStatus-COMM_FAILURE',
  SPP_HEATPUMP_INFO_STATUS_FAIL_PUBLIC_INTERNET: 'HeatPumpStatus-FAIL_PUBLIC_INTERNET',
  SPP_HEATPUMP_INFO_STATUS_FAULT: 'HeatPumpStatus-FAULT',
  SPP_HEATPUMP_INFO_STATUS_UNKNOWN: 'HeatPumpStatus-UNKNOWN',
}

type Values = Partial<PortValuesHeatPump> | undefined

function validNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/**
 * Heat output, W: the heat pump circuit (`thermal_power`) plus the electric
 * heaters (`aux_power`), which turn all their electricity into heat inside the
 * house. Null when the unit does not report `thermal_power`.
 */
export function heatPumpHeat(values: Values): number | null {
  const thermal = values?.thermal_power
  if (!validNumber(thermal)) {
    return null
  }
  const aux = values?.aux_power
  return thermal + (validNumber(aux) && aux > 0 ? aux : 0)
}

/**
 * Coefficient of performance = heat output / electrical power, only when both
 * are reported and the electrical power is positive. `power` is the whole
 * unit's input and already includes the heaters (spp_heatpump_info_t.cur_power),
 * so a heater alone gives COP 1.
 */
export function heatPumpCop(values: Values): number | null {
  const electrical = values?.power
  const heat = heatPumpHeat(values)
  if (!validNumber(electrical) || heat === null || electrical <= 0) {
    return null
  }
  return heat / electrical
}

/** The shown temperatures that the unit reports, as [field, translation key, degC]. */
export function heatPumpTemperatures(values: Values): [HeatPumpTemperature, string, number][] {
  const ret: [HeatPumpTemperature, string, number][] = []
  for (const [field, key] of HEAT_PUMP_SHOWN_TEMPERATURES) {
    const value = values?.[field]
    if (validNumber(value)) {
      ret.push([field, key, value])
    }
  }
  return ret
}

/** Translation keys of the shown flags that are set. */
export function heatPumpActiveFlags(values: Values): string[] {
  const flags = values?.flags || {}
  return HEAT_PUMP_SHOWN_FLAGS.filter(([flag]) => flags[flag]).map(([, key]) => key)
}

/** Translation key of the operating mode, or null before the first info message. */
export function heatPumpModeKey(values: Values): string | null {
  return values?.mode ? HEAT_PUMP_MODE_KEYS[values.mode] ?? HEAT_PUMP_MODE_KEYS.HEATPUMP_MODE_UNKNOWN : null
}

/** Translation key of the communication status, or null before the first info message. */
export function heatPumpStatusKey(values: Values): string | null {
  return values?.status ? HEAT_PUMP_STATUS_KEYS[values.status] ?? HEAT_PUMP_STATUS_KEYS.SPP_HEATPUMP_INFO_STATUS_UNKNOWN : null
}

/**
 * Fault code text when the machine reports a fault (status 3), else null.
 * Midea: low byte = reg 124 (error code table 1), high byte = low byte of reg 188 (table 2).
 */
export function heatPumpFault(values: Values): string | null {
  if (values?.status !== 'SPP_HEATPUMP_INFO_STATUS_FAULT') return null
  const code = values.fault_code ?? 0
  return `${code & 0xff} / ${(code >> 8) & 0xff}`
}

/**
 * The electrical input target the driver is tracking, as a translation key or
 * watts: released to the machine's own control, space heating off, or a power.
 * Null before the first info message.
 */
export function heatPumpTarget(values: Values): { key: string } | { watts: number, dutyCycling: boolean } | null {
  if (!values?.status || values.target_power === undefined) return null
  if (values.target_power === null) return { key: 'HeatPump-Strategy-MANUAL' }
  if (values.target_power === 0) return { key: 'HeatPump-Space heating off' }
  return { watts: values.target_power, dutyCycling: !!values.tracker?.duty_cycling }
}

/** Whether the heat pump link reports a problem (anything but OK, once reported). */
export function heatPumpHasProblem(values: Values): boolean {
  return !!values?.status && values.status !== 'SPP_HEATPUMP_INFO_STATUS_OK'
}
